// Filename-sanitizing primitives shared by the on-demand result download helpers (CSV, PDF, Word, and any
// future one): illegal-character replacement, whitespace collapse, control-char strip, trailing
// dot/space strip, and code-point-safe truncation. Extracted so the exact same rules can't drift
// between download types.

// Characters a file name should not carry: those Windows or POSIX reject outright, plus `%` (legal,
// but it survives into URLs and logs as an escape). Replaced rather than dropped, so two names
// differing only in punctuation can't collapse into one. Spaces are deliberately kept: they are legal
// everywhere, and drawing names are full of them.
const ILLEGAL_FILE_NAME_CHARS = /[/\\?%*:|"<>]/g
// Unicode control category — invisible in the UI, so they can be pasted into a name unnoticed.
// Applied AFTER the whitespace collapse, so the whitespace controls (tab, newline, CR) have already
// become spaces rather than being deleted and running two words together.
const CONTROL_CHARS = /\p{Cc}/gu
// Windows rejects a trailing dot or space, and both are invisible mistakes at the end of a name.
const TRAILING_DOTS_AND_SPACES = /[.\s]+$/

// Cuts to the length cap on code points rather than UTF-16 units, so the cap can't fall in the middle
// of a surrogate pair and leave a lone surrogate (which some file systems reject) in the name.
export const truncateByCodePoint = (value: string, maxLength: number) =>
	Array.from(value).slice(0, maxLength).join("")

export const sanitizeFileNamePart = (value: string, maxLength: number) =>
	truncateByCodePoint(
		value
			.replace(ILLEGAL_FILE_NAME_CHARS, "-")
			// Collapses whitespace runs into a single space.
			.replace(/\s+/g, " ")
			.replace(CONTROL_CHARS, "")
			.trim()
			// A leading dot would make the download a hidden file on POSIX.
			.replace(/^\.+/, ""),
		maxLength,
	).replace(TRAILING_DOTS_AND_SPACES, "")

// Drawing names carry their upload extension ("Block C.pdf"); it has no business in the middle of a
// download name composed from it.
export const stripPdfExtension = (value: string) => value.replace(/\.pdf$/i, "")
